import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SearchMode, SearchResponse, SearchResult } from "../../shared/types";
import { apiSearch, clearRecent, pushRecent, readRecent, type RecentEntry } from "../lib/api";
import { cleanDisplayName, detectQueryKind, digits, formatCnpjInput } from "../lib/format";
import { groupPartnerResults } from "../lib/groupPartners";
import { navigate, reflectSearch } from "../lib/useHashRoute";
import { StatusBadge } from "./StatusBadge";
import { IconAlert, IconArrowRight, IconBuilding, IconSearch, IconUser, IconWeb } from "./Icons";

/** Modos visíveis na interface. A busca por CPF completo saiu de cena:
 *  a Receita só publica o CPF mascarado, então ela mais confundia que ajudava. */
type UiMode = Exclude<SearchMode, "cpf">;

const MODES: Array<{ id: UiMode; label: string }> = [
  { id: "auto", label: "Automático" },
  { id: "cnpj", label: "CNPJ" },
  { id: "company", label: "Empresa" },
  { id: "partner", label: "Sócio" },
];

const EXAMPLES: Array<{ label: string; mode: UiMode; q: string }> = [
  { label: "Google Brasil", mode: "company", q: "Google Brasil Internet" },
  { label: "Nubank", mode: "company", q: "Nu Pagamentos" },
  { label: "IBM", mode: "company", q: "IBM Brasil" },
  { label: "Banco do Brasil", mode: "cnpj", q: "00000000000191" },
];

const PLACEHOLDER: Record<UiMode, string> = {
  auto: "Busque por CNPJ, razão social, nome fantasia ou sócio",
  cnpj: "00.000.000/0000-00",
  company: "Razão social ou nome fantasia",
  partner: "Nome completo do sócio ou administrador",
};

const PAGE_SIZE = 6;

type Props = {
  initialMode?: SearchMode;
  initialQuery?: string;
};

function ResultCard({
  result,
  onOpen,
  onNetwork,
}: {
  result: SearchResult;
  onOpen: (result: SearchResult) => void;
  onNetwork: (result: SearchResult, event: React.MouseEvent) => void;
}) {
  const clickable = digits(result.cnpj ?? "").length === 14;
  return (
    <button
      className={`result-card ${clickable ? "" : "static"}`}
      onClick={() => clickable && onOpen(result)}
      title={`Fonte: ${result.source}`}
    >
      <span className="result-avatar" aria-hidden>
        {result.kind === "partner" ? <IconUser size={18} /> : <IconBuilding size={18} />}
      </span>
      <span className="result-main">
        <span className="result-name">
          {result.partnerName ? result.partnerName : cleanDisplayName(result.name)}
          {result.maskMatch && <> <span className="badge match">Máscara confere</span></>}
        </span>
        {result.partnerName ? (
          <span className="result-sub">
            {result.role ?? "Vínculo societário"} em <strong>{cleanDisplayName(result.name)}</strong>
            {result.subtitle ? ` (${cleanDisplayName(result.subtitle)})` : ""}
          </span>
        ) : (
          result.subtitle && <span className="result-sub">{cleanDisplayName(result.subtitle)}</span>
        )}
        <span className="result-meta">
          {result.cnpj && <span className="mono">{result.cnpj}</span>}
          {(result.city || result.state) && (
            <span>{[result.city, result.state].filter(Boolean).join(" · ")}</span>
          )}
          {result.joinedAt && <span>No QSA desde {result.joinedAt}</span>}
          {result.maskedDocument && <span className="mono">{result.maskedDocument}</span>}
        </span>
        {result.matchNote && !result.maskMatch && (
          <span className="result-note">{result.matchNote}</span>
        )}
      </span>
      <span className="result-side">
        <StatusBadge status={result.status} />
        {clickable && (
          <span
            className="btn btn-sm btn-secondary result-network-btn"
            role="button"
            tabIndex={0}
            onClick={(event) => onNetwork(result, event)}
            onKeyDown={(event) => {
              if (event.key === "Enter") onNetwork(result, event as unknown as React.MouseEvent);
            }}
            title="Abrir direto a teia societária"
          >
            <IconWeb size={14} /> Teia
          </span>
        )}
      </span>
    </button>
  );
}

/** Cartão de pessoa: um sócio com todas as empresas em que aparece.
 *  O cabeçalho abre a teia da pessoa (a rede da primeira empresa, onde os
 *  vínculos dela aparecem em profundidade 2). */
function PersonCard({
  personName,
  maskedDocument,
  maskMatch,
  sources,
  companies,
  onOpen,
  onNetwork,
  onOpenWeb,
  onOpenPerson,
}: {
  personName: string;
  maskedDocument?: string;
  maskMatch: boolean;
  sources: string[];
  companies: SearchResult[];
  onOpen: (result: SearchResult) => void;
  onNetwork: (result: SearchResult, event: React.MouseEvent) => void;
  onOpenWeb: (company: SearchResult) => void;
  onOpenPerson: (name: string, doc?: string) => void;
}) {
  const anchor = companies.find((company) => digits(company.cnpj ?? "").length === 14);
  return (
    <article className="person-card">
      <button
        className="person-head clickable"
        onClick={() => onOpenPerson(personName, maskedDocument)}
        title={`Abrir o perfil de ${cleanDisplayName(personName)}${sources.length ? ` · fonte: ${sources.join(" + ")}` : ""}`}
      >
        <span className="result-avatar" aria-hidden><IconUser size={18} /></span>
        <span className="person-id">
          <span className="result-name">
            {cleanDisplayName(personName)}
            {maskMatch && <> <span className="badge match">Máscara confere</span></>}
          </span>
          <span className="result-meta">
            <span>{companies.length === 1 ? "1 empresa no QSA" : `${companies.length} empresas no QSA`}</span>
            {maskedDocument && <span className="mono">{maskedDocument}</span>}
          </span>
        </span>
        <span className="person-open" aria-hidden>
          <IconUser size={14} /> Ver perfil <IconArrowRight size={13} />
        </span>
        {anchor && (
          <span
            className="person-open teia"
            role="button"
            tabIndex={0}
            onClick={(event) => {
              event.stopPropagation();
              onOpenWeb(anchor);
            }}
            title={`Abrir a teia de ${cleanDisplayName(personName)}`}
          >
            <IconWeb size={14} /> Teia
          </span>
        )}
      </button>
      <div className="person-companies">
        {companies.map((company) => {
          const clickable = digits(company.cnpj ?? "").length === 14;
          return (
            <button
              key={company.id}
              className={`person-company ${clickable ? "" : "static"}`}
              onClick={() => clickable && onOpen(company)}
            >
              <span className="person-company-icon" aria-hidden><IconBuilding size={15} /></span>
              <span className="person-company-main">
                <span className="person-company-name">{cleanDisplayName(company.name)}</span>
                <span className="result-meta">
                  {company.role && <span>{company.role}</span>}
                  {company.cnpj && <span className="mono">{company.cnpj}</span>}
                  {(company.city || company.state) && (
                    <span>{[company.city, company.state].filter(Boolean).join(" · ")}</span>
                  )}
                  {company.joinedAt && <span>No QSA desde {company.joinedAt}</span>}
                </span>
              </span>
              <span className="result-side">
                <StatusBadge status={company.status} />
                {clickable && (
                  <span
                    className="btn btn-sm btn-secondary result-network-btn"
                    role="button"
                    tabIndex={0}
                    onClick={(event) => onNetwork(company, event)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") onNetwork(company, event as unknown as React.MouseEvent);
                    }}
                    title="Abrir direto a teia societária"
                  >
                    <IconWeb size={14} /> Teia
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </article>
  );
}

/** Grupo de sócios: resultados agrupados por pessoa, com paginação por pessoa. */
function PartnerGroup({
  title,
  results,
  onOpen,
  onNetwork,
  onOpenWeb,
  onOpenPerson,
}: {
  title: string;
  results: SearchResult[];
  onOpen: (result: SearchResult) => void;
  onNetwork: (result: SearchResult, event: React.MouseEvent) => void;
  onOpenWeb: (company: SearchResult) => void;
  onOpenPerson: (name: string, doc?: string) => void;
}) {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const { groups, unidentified } = useMemo(() => groupPartnerResults(results), [results]);
  if (!groups.length && !unidentified.length) return null;
  const shown = groups.slice(0, visible);
  const remaining = groups.length - shown.length;
  return (
    <>
      {groups.length > 0 && (
        <section className="result-group">
          <h2 className="result-group-title">
            {title} <span className="result-group-count">{groups.length}</span>
          </h2>
          {shown.map((group) => (
            <PersonCard
              key={group.key}
              personName={group.personName}
              maskedDocument={group.maskedDocument}
              maskMatch={group.maskMatch}
              sources={group.sources}
              companies={group.companies}
              onOpen={onOpen}
              onNetwork={onNetwork}
              onOpenWeb={onOpenWeb}
              onOpenPerson={onOpenPerson}
            />
          ))}
          {remaining > 0 && (
            <button className="btn btn-secondary btn-sm load-more" onClick={() => setVisible((count) => count + 10)}>
              Mostrar mais {Math.min(remaining, 10)} de {remaining}
            </button>
          )}
        </section>
      )}
      {unidentified.length > 0 && (
        <ResultGroup
          title="Empresas com sócio de nome parecido"
          note="A fonte diz que alguém com esse nome está no quadro societário, mas não publica qual sócio é. Abra a ficha para ver o nome completo."
          results={unidentified}
          onOpen={onOpen}
          onNetwork={onNetwork}
        />
      )}
    </>
  );
}

function ResultGroup({
  title,
  note,
  results,
  onOpen,
  onNetwork,
}: {
  title: string;
  note?: string;
  results: SearchResult[];
  onOpen: (result: SearchResult) => void;
  onNetwork: (result: SearchResult, event: React.MouseEvent) => void;
}) {
  const [visible, setVisible] = useState(PAGE_SIZE);
  if (!results.length) return null;
  const shown = results.slice(0, visible);
  const remaining = results.length - shown.length;
  return (
    <section className="result-group">
      <h2 className="result-group-title">
        {title} <span className="result-group-count">{results.length}</span>
      </h2>
      {note && <p className="result-group-note">{note}</p>}
      {shown.map((result) => (
        <ResultCard key={result.id} result={result} onOpen={onOpen} onNetwork={onNetwork} />
      ))}
      {remaining > 0 && (
        <button className="btn btn-secondary btn-sm load-more" onClick={() => setVisible((count) => count + 10)}>
          Mostrar mais {Math.min(remaining, 10)} de {remaining}
        </button>
      )}
    </section>
  );
}

export function SearchHome({ initialMode, initialQuery }: Props) {
  const [mode, setMode] = useState<UiMode>(initialMode === "cpf" ? "partner" : initialMode ?? "auto");
  const [query, setQuery] = useState(() => {
    if (initialMode === "cnpj" && initialQuery) return formatCnpjInput(initialQuery);
    return initialQuery ?? "";
  });
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState<RecentEntry[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => setRecent(readRecent()), []);

  // CPF completo digitado na aba Tudo: explicamos em vez de buscar.
  const cpfTyped = useMemo(() => mode === "auto" && detectQueryKind(query) === "cpf", [mode, query]);

  const effectiveType = useMemo((): "cnpj" | "company" | "partner" | "auto" => {
    if (mode === "auto") {
      return detectQueryKind(query) === "cnpj" ? "cnpj" : "auto";
    }
    return mode;
  }, [mode, query]);

  const runSearch = useCallback(
    (type: "cnpj" | "company" | "partner" | "auto", term: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      reflectSearch(mode === "auto" ? "auto" : type, term);
      apiSearch(type, term, undefined, controller.signal)
        .then((payload) => {
          setResponse(payload);
          setLoading(false);
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          setLoading(false);
          setResponse({
            results: [],
            message: error instanceof Error ? error.message : "Não foi possível buscar agora.",
          });
        });
    },
    [mode],
  );

  const triggerSearch = useCallback(() => {
    const term = query.trim();
    if (cpfTyped) return false;
    if (effectiveType === "cnpj") {
      if (digits(term).length !== 14) return false;
      runSearch("cnpj", term);
      return true;
    }
    if (term.length < 3) return false;
    runSearch(effectiveType, term);
    return true;
  }, [effectiveType, cpfTyped, query, runSearch]);

  /**
   * Quem busca decide quando buscar.
   *
   * A busca saía sozinha 550 ms depois da última tecla, o que parece ágil para
   * quem digita rápido e é hostil para quem não digita: o nome sai pela metade,
   * a tela responde "nenhum resultado", e a pessoa lê isso como resposta à
   * pergunta que ainda nem terminou de fazer. Cada disparo desses também gasta
   * uma consulta nas fontes públicas, que são limitadas.
   *
   * O campo agora só limpa a tela quando fica vazio; buscar é no botão ou no
   * Enter.
   */
  useEffect(() => {
    if (query.trim()) return;
    setResponse(null);
    setLoading(false);
  }, [query]);

  const onQueryChange = (value: string) => {
    if (mode === "cnpj" || (mode === "auto" && detectQueryKind(value) === "cnpj")) {
      setQuery(formatCnpjInput(value));
      return;
    }
    setQuery(value);
  };

  const openResult = useCallback((result: SearchResult) => {
    const clean = digits(result.cnpj ?? "");
    if (clean.length !== 14) return;
    pushRecent({ cnpj: clean, name: cleanDisplayName(result.name) });
    navigate({ page: "company", cnpj: clean });
  }, []);

  const openNetwork = useCallback((result: SearchResult, event: React.MouseEvent) => {
    event.stopPropagation();
    const clean = digits(result.cnpj ?? "");
    if (clean.length !== 14) return;
    pushRecent({ cnpj: clean, name: cleanDisplayName(result.name) });
    navigate({ page: "network", cnpj: clean });
  }, []);

  const openPersonProfile = useCallback((personName: string, personDoc?: string) => {
    navigate({ page: "person", name: personName, doc: personDoc });
  }, []);

  // A teia da pessoa: abre a rede pela primeira empresa dela; na profundidade
  // padrão (2) os demais vínculos da pessoa entram pelo próprio grafo.
  const openPersonWeb = useCallback((company: SearchResult) => {
    const clean = digits(company.cnpj ?? "");
    if (clean.length !== 14) return;
    pushRecent({ cnpj: clean, name: cleanDisplayName(company.name) });
    navigate({ page: "network", cnpj: clean });
  }, []);

  // Ordem dos blocos: pessoa primeiro só quando o termo bate com o nome de um
  // sócio identificado. Vínculo sem nome não pode empurrar as empresas para
  // baixo — era o que fazia "claro" mostrar 20 sócios antes da Claro S.A.
  const groups = response?.groups;
  const partnersFirst = useMemo(() => {
    if (!groups?.partners.length) return false;
    const term = query.trim().toLowerCase().replace(/\s+/g, " ");
    if (!term) return false;
    return groups.partners.some((partner) => {
      const name = (partner.partnerName ?? "").toLowerCase().trim();
      if (!name || !name.includes(" ")) return false;
      return name.includes(term) || term.includes(name);
    });
  }, [groups, query]);

  return (
    <main className={`hero ${response || loading ? "searching" : ""}`}>
      <h1 className="hero-title">Quem está por trás do CNPJ?</h1>
      <p className="hero-subtitle">
        Digite um CNPJ, o nome de uma empresa ou de um sócio e veja quem se conecta a quem nos
        dados abertos da Receita Federal.
      </p>

      <div className="search-panel">
        <div className="mode-row" role="tablist" aria-label="Tipo de busca">
          {MODES.map((item) => (
            <button
              key={item.id}
              role="tab"
              aria-selected={mode === item.id}
              className={`chip ${mode === item.id ? "active" : ""}`}
              onClick={() => {
                setMode(item.id);
                setQuery("");
                setResponse(null);
                reflectSearch("auto", "");
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="search-box">
          {loading ? <span className="spinner" aria-label="Buscando" /> : <IconSearch size={17} />}
          <input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") triggerSearch();
            }}
            placeholder={PLACEHOLDER[mode]}
            autoFocus
            inputMode={mode === "cnpj" ? "numeric" : "text"}
            aria-label="Termo de busca"
          />
          {/* No celular o rótulo some e sobra só o ícone: sem aria-label o botão
              fica sem nome para quem usa leitor de tela. */}
          <button className="btn btn-primary" onClick={triggerSearch} disabled={loading} aria-label="Buscar">
            <span className="btn-label">Buscar</span>
            <IconArrowRight size={15} />
          </button>
        </div>

        {cpfTyped && (
          <p className="search-hint">
            Isso parece um CPF. A Receita publica só a versão <strong>mascarada</strong> do CPF
            (***XXXXXX**), então uma busca por CPF completo não tem como ser confiável e foi
            desativada. Busque pelo <strong>nome do sócio</strong> que a Teia cruza os vínculos.
          </p>
        )}
        {mode === "auto" && !query && (
          <p className="search-hint">
            Digite um CNPJ (14 dígitos) ou um nome. Nomes são procurados ao mesmo tempo entre
            empresas e sócios.
          </p>
        )}
      </div>

      {response && (
        <div className="results" role="region" aria-label="Resultados da busca">
          {response.demo && (
            <div className="demo-banner">
              <IconAlert size={16} />
              <span>Modo demonstração: as fontes públicas não responderam, exibindo snapshot local.</span>
            </div>
          )}
          <p className="results-message">{response.message}</p>

          {groups ? (
            partnersFirst ? (
              <>
                <PartnerGroup title="Sócios e administradores" results={groups.partners} onOpen={openResult} onNetwork={openNetwork} onOpenWeb={openPersonWeb} onOpenPerson={openPersonProfile} />
                <ResultGroup title="Empresas" results={groups.companies} onOpen={openResult} onNetwork={openNetwork} />
              </>
            ) : (
              <>
                <ResultGroup title="Empresas" results={groups.companies} onOpen={openResult} onNetwork={openNetwork} />
                <PartnerGroup title="Sócios e administradores" results={groups.partners} onOpen={openResult} onNetwork={openNetwork} onOpenWeb={openPersonWeb} onOpenPerson={openPersonProfile} />
              </>
            )
          ) : response.results[0]?.kind === "partner" ? (
            <PartnerGroup
              title="Sócios e administradores"
              results={response.results}
              onOpen={openResult}
              onNetwork={openNetwork}
              onOpenWeb={openPersonWeb}
              onOpenPerson={openPersonProfile}
            />
          ) : (
            <ResultGroup
              title="Resultados"
              results={response.results}
              onOpen={openResult}
              onNetwork={openNetwork}
            />
          )}
        </div>
      )}

      {!response && !loading && (
        <>
          <div className="examples">
            {EXAMPLES.map((example) => (
              <button
                key={example.label}
                className="example-chip"
                onClick={() => {
                  setMode(example.mode);
                  setQuery(example.mode === "cnpj" ? formatCnpjInput(example.q) : example.q);
                }}
              >
                {example.label}
              </button>
            ))}
          </div>
          {recent.length > 0 && (
            <div className="results">
              <p className="results-message recent-head">
                Vistos recentemente
                <button
                  className="link-btn"
                  onClick={() => {
                    clearRecent();
                    setRecent([]);
                  }}
                >
                  Limpar
                </button>
              </p>
              {recent.map((entry) => (
                <button
                  key={entry.cnpj}
                  className="result-card"
                  onClick={() => navigate({ page: "company", cnpj: entry.cnpj })}
                >
                  <span className="result-avatar" aria-hidden><IconBuilding size={18} /></span>
                  <span className="result-main">
                    <span className="result-name">{cleanDisplayName(entry.name)}</span>
                    <span className="result-meta">
                      <span className="mono">{formatCnpjInput(entry.cnpj)}</span>
                    </span>
                  </span>
                  <span className="result-side">
                    <span
                      className="btn btn-sm btn-secondary result-network-btn"
                      role="button"
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate({ page: "network", cnpj: entry.cnpj });
                      }}
                      title="Abrir direto a teia societária"
                    >
                      <IconWeb size={14} /> Teia
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </main>
  );
}
